import type { PublicNotification } from "@dropl/shared";
import { api } from "../api/index.js";
import { connectUser, type UserSocket } from "../socket/index.js";
import { toast } from "../ui/overlay.js";
import { t } from "../i18n/index.js";

interface State {
  items: PublicNotification[];
  unread: number;
  loaded: boolean;
}

const state: State = { items: [], unread: 0, loaded: false };
const listeners = new Set<(s: State) => void>();
let socket: UserSocket | null = null;

function snapshot(): State {
  return { items: [...state.items], unread: state.unread, loaded: state.loaded };
}

function emit(): void {
  const s = snapshot();
  for (const cb of listeners) cb(s);
}

export function subscribe(cb: (s: State) => void): () => void {
  listeners.add(cb);
  cb(snapshot());
  return () => listeners.delete(cb);
}

export function getState(): State {
  return snapshot();
}

export async function refresh(): Promise<void> {
  try {
    const data = await api<{ items: PublicNotification[]; unread: number }>("/api/notifications?limit=30");
    state.items = data.items;
    state.unread = data.unread;
    state.loaded = true;
    emit();
  } catch {

    state.loaded = true;
    emit();
  }
}

export async function markRead(id: string): Promise<void> {
  const it = state.items.find((n) => n.id === id);
  if (!it || it.readAt) return;

  it.readAt = new Date().toISOString();
  state.unread = Math.max(0, state.unread - 1);
  emit();
  try {
    await api(`/api/notifications/${encodeURIComponent(id)}/read`, { method: "POST", body: {} });
  } catch {
    it.readAt = null;
    state.unread += 1;
    emit();
  }
}

export async function remove(id: string): Promise<void> {
  const idx = state.items.findIndex((n) => n.id === id);
  if (idx === -1) return;
  const removed = state.items[idx];
  state.items = state.items.filter((_, i) => i !== idx);
  if (removed && !removed.readAt) state.unread = Math.max(0, state.unread - 1);
  emit();
  try {
    await api(`/api/notifications/${encodeURIComponent(id)}`, { method: "DELETE" });
  } catch {

    void refresh();
  }
}

export async function markAllRead(): Promise<void> {
  const wasUnread = state.unread;
  if (wasUnread === 0) return;
  const nowIso = new Date().toISOString();
  for (const n of state.items) if (!n.readAt) n.readAt = nowIso;
  state.unread = 0;
  emit();
  try {
    await api("/api/notifications/read-all", { method: "POST", body: {} });
  } catch {

  }
}

export function attachUserSocket(): void {
  if (socket) return;
  socket = connectUser();
  socket.on("user:notification", (n) => {
    state.items = [n, ...state.items].slice(0, 100);
    if (!n.readAt) state.unread += 1;
    emit();
    playPing();
    showToast(n);
  });
  socket.on("user:legal.updated", () => {

    void import("../ui/legal-gate.js").then((m) => m.enforceLegalAcceptance());
  });
  socket.on("disconnect", () => {

  });
}

export function detachUserSocket(): void {
  if (!socket) return;
  socket.disconnect();
  socket = null;
  state.items = [];
  state.unread = 0;
  state.loaded = false;
  emit();
}

function showToast(n: PublicNotification): void {
  const msg = n.body ? `${n.title} · ${n.body}` : n.title;
  if (n.kind === "success") toast.success(msg);
  else if (n.kind === "warning") toast.warn(msg);
  else if (n.kind === "error") toast.error(msg);
  else toast.info(msg);
}

let audioCtx: AudioContext | null = null;
function playPing(): void {
  try {
    type Ctor = typeof AudioContext;
    const Ctor =
      (window.AudioContext as Ctor | undefined) ??
      ((window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext);
    if (!Ctor) return;
    if (!audioCtx) audioCtx = new Ctor();
    if (audioCtx.state === "suspended") void audioCtx.resume();
    const now = audioCtx.currentTime;

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    gain.connect(audioCtx.destination);

    const o1 = audioCtx.createOscillator();
    o1.type = "sine";
    o1.frequency.setValueAtTime(880, now);
    o1.connect(gain);
    o1.start(now);
    o1.stop(now + 0.45);

    const o2 = audioCtx.createOscillator();
    o2.type = "sine";
    o2.frequency.setValueAtTime(1320, now + 0.06);
    o2.connect(gain);
    o2.start(now + 0.06);
    o2.stop(now + 0.45);
  } catch {  }
}

export function kindIcon(kind: PublicNotification["kind"]): string {
  switch (kind) {
    case "success": return "fa-circle-check";
    case "warning": return "fa-triangle-exclamation";
    case "error":   return "fa-circle-exclamation";
    default:        return "fa-circle-info";
  }
}

export function kindLabel(kind: PublicNotification["kind"]): string {
  return t(`notifications.kind.${kind}`);
}
